import React, { useState, useCallback, useMemo } from 'react';
import { ConfirmDialog, ConfirmDialogVariant } from './ConfirmDialog';

interface ConfirmDialogOptions {
  title: string;
  message: string | React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  variant?: ConfirmDialogVariant;
  onConfirm: () => void | Promise<void>;
  onCancel?: () => void;
}

interface UseConfirmDialogReturn {
  confirm: (options: ConfirmDialogOptions) => void;
  ConfirmDialogComponent: React.ReactNode;
}

/**
 * Hook for easily showing confirmation dialogs
 *
 * @example
 * const { confirm, ConfirmDialogComponent } = useConfirmDialog();
 *
 * const handleDelete = () => {
 *   confirm({
 *     title: 'Delete Tool',
 *     message: 'Are you sure you want to delete this tool? This action cannot be undone.',
 *     confirmText: 'Delete',
 *     variant: 'danger',
 *     onConfirm: async () => {
 *       await deleteTool(id);
 *       showSuccess('Tool deleted successfully');
 *     }
 *   });
 * };
 *
 * return (
 *   <>
 *     <Button onClick={handleDelete}>Delete</Button>
 *     {ConfirmDialogComponent}
 *   </>
 * );
 */
export function useConfirmDialog(): UseConfirmDialogReturn {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<ConfirmDialogOptions | null>(null);

  const confirm = useCallback((newOptions: ConfirmDialogOptions) => {
    setOptions(newOptions);
    setIsOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    if (options?.onCancel) {
      options.onCancel();
    }
  }, [options]);

  const handleConfirm = useCallback(async () => {
    if (options?.onConfirm) {
      await options.onConfirm();
    }
  }, [options]);

  const ConfirmDialogComponent = useMemo(() => {
    if (!options) return null;

    return (
      <ConfirmDialog
        open={isOpen}
        onClose={handleClose}
        onConfirm={handleConfirm}
        title={options.title}
        message={options.message}
        confirmText={options.confirmText}
        cancelText={options.cancelText}
        variant={options.variant}
      />
    );
  }, [isOpen, options, handleClose, handleConfirm]);

  return {
    confirm,
    ConfirmDialogComponent,
  };
}
